import type { InvoiceModel } from '@/Models/InvoiceModel';
import { MenuItem, Select, Tooltip } from '@mui/material';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import React, { ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as API from '../../API';
import type NomenclatureMotivationType from '../../Models/NomenclatureMotivationType';
import type OrderNomenclature from '../../Models/OrderNomenclature';
import type SelectListItem from '../../Models/SelectListItem';
import SupplementType from '../../Models/SupplementType';
import Styles from './NomenclatureTableGrid.module.scss';

interface NomenclatureTableGridProps {
    invoiceModel: InvoiceModel;
    nomenclatures: OrderNomenclature[];
    totalCustomHonorarium: string;
    changeNomenclatures: (arg0: OrderNomenclature[]) => void;
    checkNumericOnly: (event: React.KeyboardEvent<HTMLDivElement>, valueToCheck: string) => void;
    updateInvoiceModel: Function;
    setTotalCustomHonorarium: Function;
    getInvoiceInformation: Function;
}

function NomenclatureTableGrid({
    invoiceModel,
    nomenclatures,
    totalCustomHonorarium,
    changeNomenclatures,
    checkNumericOnly,
    updateInvoiceModel,
    setTotalCustomHonorarium,
    getInvoiceInformation,
}: Readonly<NomenclatureTableGridProps>): ReactElement {
    const { t } = useTranslation();
    const [allMotivations, setAllMotivations] = useState<SelectListItem[]>([]);

    useEffect(() => {
        API.getSelectDropDownForMotivations()
            .then((selects: { Motivations: SelectListItem[] }) => {
                setAllMotivations(selects.Motivations);
            })
            .catch(() => {
                console.error('Something went wrong while getting all motivations for dropdown');
            });
    }, []);

    return (
        <Grid size={12} className={Styles.nomenclatureGrid}>
            <table id="nomenclatureTable" className="regularTable nomenclatureTable">
                <thead>
                    <tr>
                        {/* TextAlign left is necessary here to overwrite datatables.css overzealous styling... */}
                        <th className={`${Styles.sWidth} center`}>{t('Code')}</th>
                        <th className={`${Styles.sWidth} center`}>{t('Nomenclature')}</th>
                        <th className={`${Styles.lWidth} center`}>{t('Description')}</th>
                        <th className={`${Styles.sWidth} center`}>{t('Honorarium')}</th>
                        <th className={`${Styles.sWidth} center`}>{t('WIGW')}</th>
                        <th className={`${Styles.sWidth} center`}>{t('Other')}</th>
                        <th className={`${Styles.sWidth} center`}>{t('Practice')}</th>
                        <th className={`${Styles.xsWidth} center`}>{t('Radiologist')}</th>
                        <th className={`${Styles.sWidth} center`}>{t('Motivation')}</th>
                        <th className={`${Styles.xsWidth} center`} />
                    </tr>
                </thead>
                <tbody>
                    <NomenclaturesTableBody
                        invoiceModel={invoiceModel}
                        allMotivations={allMotivations}
                        nomenclatures={nomenclatures}
                        totalCustomHonorarium={totalCustomHonorarium}
                        changeNomenclatures={changeNomenclatures}
                        checkNumericOnly={checkNumericOnly}
                        updateInvoiceModel={updateInvoiceModel}
                        setTotalCustomHonorarium={setTotalCustomHonorarium}
                        getInvoiceInformation={getInvoiceInformation}
                    />
                </tbody>
            </table>
        </Grid>
    );
}

type NomenclaturesTableBodyProps = NomenclatureTableGridProps & { allMotivations: SelectListItem[] };
function NomenclaturesTableBody(props: Readonly<NomenclaturesTableBodyProps>): ReactElement {
    if (props.invoiceModel.FreeOfCharge) {
        return <FreeOfChargeRow />;
    }

    if (!props.nomenclatures || props.nomenclatures.length === 0) {
        return <EmptyRow />;
    }

    return <NomenclaturesTableRows {...props} />;
}

function NomenclaturesTableRows({
    invoiceModel,
    nomenclatures,
    totalCustomHonorarium,
    changeNomenclatures,
    checkNumericOnly,
    updateInvoiceModel,
    setTotalCustomHonorarium,
    getInvoiceInformation,
    allMotivations,
}: Readonly<NomenclaturesTableBodyProps>): ReactElement {
    return (
        <>
            {nomenclatures.map((x, index) => (
                <OrderNomenclatureRow
                    index={index}
                    key={x.ID}
                    nomen={x}
                    motivations={allMotivations}
                    nomenclatures={nomenclatures}
                    totalCustomHonorarium={totalCustomHonorarium}
                    changeNomenclatures={changeNomenclatures}
                    checkNumericOnly={checkNumericOnly}
                    updateInvoiceModel={updateInvoiceModel}
                    setTotalCustomHonorarium={setTotalCustomHonorarium}
                    getInvoiceInformation={getInvoiceInformation}
                />
            ))}
            <tr className="totalRow">
                <td className="emptyCell" />
                <td className="emptyCell" />
                <td className="emptyCell" />
                <td className={`${Styles.bold} center`}>€ {invoiceModel.TotalHonorarium}</td>
                <td className={`${Styles.bold} center`}>€ {invoiceModel.TotalWIGW}</td>
                <td className={`${Styles.bold} center`}>€ {invoiceModel.TotalOther}</td>
                <td className={`${Styles.bold} center`}>€ {invoiceModel.TotalPractice}</td>
                <td className="emptyCell" />
                <td className="emptyCell" />
                <td className="emptyCell" />
            </tr>
        </>
    );
}

function FreeOfChargeRow(): ReactElement {
    const { t } = useTranslation();

    return (
        <tr className="emptyTable">
            <td colSpan={10} className="center">
                {t('FreeOfCharge')}
            </td>
        </tr>
    );
}

function EmptyRow(): ReactElement {
    const { t } = useTranslation();

    return (
        <tr className="emptyTable">
            <td colSpan={10} className="center">
                {t('NoNomenclaturesFound')}
            </td>
        </tr>
    );
}

function OrderNomenclatureRow({ nomen, motivations, nomenclatures, totalCustomHonorarium, changeNomenclatures, index, checkNumericOnly, updateInvoiceModel, setTotalCustomHonorarium, getInvoiceInformation }: Readonly<{
    nomen: OrderNomenclature; motivations: SelectListItem[]; nomenclatures: OrderNomenclature[]; totalCustomHonorarium: string;
    changeNomenclatures: (arg0: OrderNomenclature[]) => void; index: number; checkNumericOnly: Function; updateInvoiceModel: Function; setTotalCustomHonorarium: Function; getInvoiceInformation: Function;
}>): React.ReactElement {
    const { t } = useTranslation();
    const [customHonorarium, setCustomHonorarium] = useState<string>(totalCustomHonorarium);

    const changeCustomHonorarium = (value: string): void => {
        setCustomHonorarium(value);
        setTotalCustomHonorarium(value);
    };

    const deleteRow = (nomen: OrderNomenclature) => () => {
        if (!nomen.IsManuallyAdded) {
            return;
        }

        const filteredNomens = nomenclatures.filter((r) => r.ID !== nomen.ID);
        changeNomenclatures(filteredNomens);
    };

    return (
        <tr className="regularTable nomenclatureTable">
            <input type="hidden" className="id" name={`InvoiceModel.Nomenclatures[${index}].ID`} value={nomen.ID} />
            <input type="hidden" className="nomenclatureNumber" name={`InvoiceModel.Nomenclatures[${index}].Number`} value={nomen.Number} />
            <input type="hidden" className="isPseudo" name={`InvoiceModel.Nomenclatures[${index}].IsPseudo`} value={String(nomen.IsPseudo)} />
            <input type="hidden" className="isManuallyAdded" name={`InvoiceModel.Nomenclatures[${index}].IsManuallyAdded`} value={String(nomen.IsManuallyAdded)} />
            <input type="hidden" className="procedureCode" name={`InvoiceModel.Nomenclatures[${index}].ProcedureCode`} value={nomen.ProcedureCode} />
            <input type="hidden" className="isProduct" name={`InvoiceModel.Nomenclatures[${index}].IsProduct`} value={String(nomen.IsProduct)} />
            <td className={`${Styles.sWidth} center ${nomen.ProcedureCode ? '' : 'emptyCell'}`}>{nomen.ProcedureCode}</td>
            <td className={`${Styles.sWidth} center ${nomen.Number ? '' : 'emptyCell'} nomenclatureNumber`}>
                <div className={Styles.nomenclatureNumberDiv}>
                    {
                        nomen.IsExtraSupplement
                            ? <></>
                            : (
                                    <>
                                        {nomen.IsProduct
                                            ? (
                                                    <span>
                                                        <Tooltip title={t('FromProduct')}>
                                                            <img src="/Content/img/MenuIcons/productsBlack.png" height="20px" alt="from product" />
                                                        </Tooltip>
                                                    </span>
                                                )
                                            : (
                                                    <span>
                                                        <Tooltip title={t('FromProcedure')}>
                                                            <img src="/Content/img/MenuIcons/orderOverviewBlack.png" height="20px" alt="from procedure" />
                                                        </Tooltip>
                                                    </span>
                                                )}
                                        {nomen.Number}
                                        <Tooltip title={(
                                            <>
                                                {nomen.Number}
                                                {' '}
                                                <br />
                                                <br />
                                                {' '}
                                                {nomen.Remarks?.replace('\r\n', '<br />')}
                                            </>
                                        )}
                                        >
                                            <span className="infoIcon clearfix">
                                        &nbsp;
                                            </span>
                                        </Tooltip>
                                    </>
                                )
                    }
                </div>
            </td>
            <td className={`${Styles.lWidth}  center`}>
                <div dangerouslySetInnerHTML={{ __html: nomen.Description }} />
                {
                    nomen.IsScreening
                        ? (
                                <Tooltip title={t('ScreeningInfo')}>
                                    <span className="infoIcon clearfix">
                                &nbsp;
                                    </span>
                                </Tooltip>
                            )
                        : <></>
                }
            </td>
            {
                nomen.IsCustomAmountSupplement
                    ? (
                            <td colSpan={4} className={`${Styles.sWidth} center custom`}>
                                €
                                <TextField
                                    sx={{ all: 'initial' }}
                                    slotProps={{ htmlInput: { className: 'customAmount dis disNone' } }}
                                    type="text"
                                    value={customHonorarium}
                                    onChange={(e) => changeCustomHonorarium(e.target.value)}
                                    onKeyDown={(e) => checkNumericOnly(e, customHonorarium)}
                                    onBlur={(e) => getInvoiceInformation()}
                                />
                            </td>
                        )
                    : (
                            <>
                                <td className={`${Styles.sWidth} center ${nomen.Honorarium ? '' : 'emptyCell'}`}>{nomen.IsSupplement && nomen.SupplementType === SupplementType.Percentage ? '%' : '€'} {nomen.Honorarium}</td>
                                <td className={`${Styles.sWidth} center ${nomen.WIGW ? '' : 'emptyCell'}`}>{nomen.IsSupplement && nomen.SupplementType === SupplementType.Percentage ? '%' : '€'} {nomen.WIGW}</td>
                                <td className={`${Styles.sWidth} center ${nomen.Other ? '' : 'emptyCell'}`}>{nomen.IsSupplement && nomen.SupplementType === SupplementType.Percentage ? '%' : '€'} {nomen.Other}</td>
                                <td className={`${Styles.sWidth} center ${nomen.Practice ? '' : 'emptyCell'}`}>{nomen.IsSupplement && nomen.SupplementType === SupplementType.Percentage ? '%' : '€'} {nomen.Practice}</td>
                            </>
                        )
            }

            <td className={`${Styles.sWidth} center ${nomen.RadiologistInfo ? '' : 'emptyCell'}`}>{nomen.RadiologistInfo?.Abbreviation}</td>
            <input type="hidden" name={`InvoiceModel.Nomenclatures[${index}].ID`} value={nomen.ID} />
            <td className={`${Styles.sWidth} center ${nomen.CanBeMotivated ? '' : 'emptyCell'}`}>{nomen.CanBeMotivated
                ? (
                        <GetMotivationCell
                            nomenclature={nomen}
                            motivationType={nomen.MotivationType}
                            motivations={motivations}
                            updateInvoiceModel={updateInvoiceModel}
                        />
                    )
                : ''}
            </td>
            <td className={`${Styles.sWidth} center ${nomen.IsManuallyAdded ? 'delete' : ''}`} onClick={deleteRow(nomen)} />
        </tr>
    );
}

function GetMotivationCell({ motivationType, motivations, nomenclature, updateInvoiceModel }: Readonly<{
    motivationType: NomenclatureMotivationType | undefined; motivations: SelectListItem[]; nomenclature: OrderNomenclature; updateInvoiceModel: Function;
}>): React.ReactElement {
    const [activeMotivation, setActiveMotivation] = useState<number>(motivationType ?? 0);

    const changeActiveMotivation = (motivationTypeID: number): void => {
        setActiveMotivation(motivationTypeID);
        nomenclature.MotivationType = motivationTypeID;
        updateInvoiceModel();
    };

    return (
        <Select
            name={`MotivationsOverrideMaximumExecutions[${nomenclature.Number}]`}
            className={`dis ${Styles.motivations}`}
            value={String(activeMotivation)}
            onChange={(e) => changeActiveMotivation(Number(e.target.value))}
        >
            {
                motivations.map((x: SelectListItem) =>
                    <MenuItem key="" value={x.Value}>{x.Text}</MenuItem>,
                )
            }
        </Select>
    );
}

export default NomenclatureTableGrid;
